import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { StoreProvider } from "@/context/StoreContext";
import Layout from "@/components/layout/Layout";

import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import CartPage from "@/pages/CartPage";
import Checkout from "@/pages/Checkout";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Account from "@/pages/Account";
import Wishlist from "@/pages/Wishlist";
import TrackOrder from "@/pages/TrackOrder";
import SharedWishlist from "@/pages/SharedWishlist";
import MeetChewieSolo from "@/pages/MeetChewieSolo";
import OurStory from "@/pages/OurStory";
import Adventures from "@/pages/Adventures";
import AdventurePost from "@/pages/AdventurePost";
import FAQ from "@/pages/FAQ";
import SizeGuide from "@/pages/SizeGuide";
import BulldogGuide from "@/pages/BulldogGuide";
import PupOfTheMonthPage from "@/pages/PupOfTheMonthPage";
import PupEntrantPage from "@/pages/PupEntrantPage";
import PupConfirmPage from "@/pages/PupConfirmPage";
import PawparazziPage from "@/pages/PawparazziPage";
import PawparazziProfilePage from "@/pages/PawparazziProfilePage";
import PawparazziInterviewPage from "@/pages/PawparazziInterviewPage";
import PupBirthdayClub from "@/pages/PupBirthdayClub";
import WallHallOfFame from "@/pages/WallHallOfFame";
import PlacesMap from "@/pages/PlacesMap";
import Contact from "@/pages/Contact";
import LegalPage from "@/pages/LegalPage";
import Admin from "@/pages/Admin";
import NotFound from "@/pages/NotFound";
import AuthCallback from "@/pages/AuthCallback";
import BundleBuilder from "@/pages/BundleBuilder";
import GiftCards from "@/pages/GiftCards";
import GiftCardSuccess from "@/pages/GiftCardSuccess";
import GiftCardBalance from "@/pages/GiftCardBalance";
import CookieBanner from "@/components/CookieBanner";
import { pixelPage } from "@/lib/pixel";

// /blog/<slug> is the URL we share on socials; posts live under /adventures
function BlogAlias() {
  const { slug } = useParams();
  return <Navigate to={`/adventures/${slug}`} replace />;
}


function RoutedApp() {
  const location = useLocation();
  // TikTok PageView on in-app route changes — a no-op until advertising consent is given
  useEffect(() => { pixelPage(); }, [location.pathname, location.search]);
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <>
    <CookieBanner />
    <Routes>
      <Route path="/admin" element={<Admin />} />
      <Route element={<Layout />}>
        <Route path="/" element={<Home />} />
        <Route path="/shop" element={<Shop />} />
        {["australia-day", "valentines-day", "st-patricks-day", "easter", "mothers-day", "fathers-day", "halloween", "christmas"].map((s) => (
          <Route key={s} path={`/${s}`} element={<Navigate to={`/shop?seasonal=${s}`} replace />} />
        ))}
        <Route path="/bundle-builder" element={<BundleBuilder />} />
        <Route path="/gift-cards" element={<GiftCards />} />
        <Route path="/gift-cards/balance" element={<GiftCardBalance />} />
        <Route path="/gift-cards/success" element={<GiftCardSuccess />} />
        <Route path="/product/:slug" element={<ProductDetail />} />
        <Route path="/cart" element={<CartPage />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/payment/success" element={<PaymentSuccess />} />
        <Route path="/payment/cancel" element={<PaymentCancel />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/account" element={<Account />} />
        <Route path="/wishlist" element={<Wishlist />} />
        <Route path="/track" element={<TrackOrder />} />
        <Route path="/wishlist/shared/:token" element={<SharedWishlist />} />
        <Route path="/meet" element={<MeetChewieSolo />} />
        <Route path="/our-story" element={<OurStory />} />
        <Route path="/adventures" element={<Adventures />} />
        <Route path="/adventures/:slug" element={<AdventurePost />} />
        <Route path="/blog/:slug" element={<BlogAlias />} />
        <Route path="/blog" element={<Navigate to="/adventures" replace />} />
        <Route path="/faq" element={<FAQ />} />
        <Route path="/size-guide" element={<SizeGuide />} />
        <Route path="/bulldog-guide" element={<BulldogGuide />} />
        <Route path="/pup-of-the-month" element={<PupOfTheMonthPage />} />
        <Route path="/pup-of-the-month/confirm" element={<PupConfirmPage />} />
        <Route path="/pup-of-the-month/:slug" element={<PupEntrantPage />} />
        <Route path="/pawparazzi-red-carpet" element={<PawparazziPage />} />
        <Route path="/pawparazzi-red-carpet/interview/:token" element={<PawparazziInterviewPage />} />
        <Route path="/pawparazzi-red-carpet/:year/:month/:slug" element={<PawparazziProfilePage />} />
        <Route path="/pup-birthday-club" element={<PupBirthdayClub />} />
        <Route path="/pup-of-the-wall" element={<WallHallOfFame />} />
        <Route path="/places" element={<PlacesMap />} />
        <Route path="/contact" element={<Contact />} />
        <Route path="/legal/:slug" element={<LegalPage />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <StoreProvider>
        <BrowserRouter>
          <Toaster position="top-center" richColors closeButton
            toastOptions={{ style: { fontFamily: "Nunito, sans-serif", borderRadius: "1rem" } }} />
          <RoutedApp />
        </BrowserRouter>
      </StoreProvider>
    </AuthProvider>
  );
}
